import { friendlyError } from '../../utils/userMessages';
import { brandPalette as palette } from '../../utils/brandPalette';
import { openSafeExternalUrl } from '../../utils/safeNavigation';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    InputAdornment,
    Divider,
    Stack,
    Badge,
    LinearProgress,
    Skeleton,
    Snackbar,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Search as SearchIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Verified as VerifiedIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Public as PublicIcon,
    Close as ClearIcon,
    Chat as ChatIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    Send as SendIcon,
    Close as CloseIcon,
    Recommend as ThumbUpIcon,
    TrendingDown as ThumbDownIcon,
    Info as InfoIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AiIcon,
    Assessment as ScoreIcon,
    Check as CheckIcon,
    NotificationsActive as NotificationIcon,
    RocketLaunch as RocketIcon,
    Menu as MoreVertIcon,
    ArrowForward as ArrowForwardIcon,
    Pending as PendingIcon,
    Link as LinkIcon,
    Description as CopyIcon,
    Email as InboxIcon,
    Send as OutboxIcon,
    Share as ShareIcon,
    Schedule as AccessTimeIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    Flag as FlagIcon,
    LocationOn as MapIcon,
    LocationOn as RoomIcon,
    Dashboard as DashboardIcon,
    AttachFile as AttachFileIcon,
    GraphicEq as MicIcon,
    VolumeOff as MicOffIcon,
    Image as ImageIcon,
    InsertDriveFile as FileIcon,
    PlayCircleRounded as PlayIcon,
    PauseCircleRounded as PauseIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    orderBy,
    where,
    doc,
    addDoc,
    serverTimestamp,
    updateDoc,
    getDoc,
    onSnapshot,
    limit,
    deleteDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import Header from '../common/Header/Header';
import LoadingSpinner from '../common/LoadingSpinner';
import { uploadFile, deleteFile } from '../../services/integrations/storage';
import { introductionService } from '../../services/workflow/communication';
import { generateMessage } from '../../services/integrations/ai';
import { getIntegrationSettings } from '../../services/integrations/backend';

// ============================================
// DESIGN TOKENS
// ============================================



const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

// ============================================
// NOTIFICATION SOUND
// ============================================

const playNotificationSound = () => {
    try {
        // Do not create/resume an AudioContext until the browser reports a user gesture.
        if (typeof window === 'undefined' || !window.navigator?.userActivation?.hasBeenActive) return;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
        console.log('Audio not supported');
    }
};

// ============================================
// PAGE LOADER
// ============================================

const PageLoader = () => (
    <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: palette.canvas,
        zIndex: 9999,
    }}>
        <style>{fontImport}</style>
        <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
            <Box sx={{ position: 'relative', width: 100, height: 100 }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                    <Box sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        border: `3px solid ${palette.hairline}`,
                        borderTop: `3px solid ${palette.rust}`,
                        borderRight: `3px solid ${palette.gold}`,
                        borderBottom: `3px solid ${palette.pine}`,
                        borderLeft: `3px solid ${palette.purple}`,
                        animation: 'spin 2s linear infinite',
                    }} />
                </motion.div>
                <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                >
                    <ChatIcon sx={{ fontSize: 36, color: palette.rust }} />
                </motion.div>
            </Box>
        </motion.div>
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{ textAlign: 'center', marginTop: '1.5rem' }}
        >
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.8rem', color: palette.ink }}>
                <span style={{ color: palette.rust }}>TELCO</span>
                <span style={{ color: palette.gold }}>FAIR</span>
            </Typography>
            <Box sx={{ width: 200, height: 3, bgcolor: palette.hairline, borderRadius: 2, mt: 2, mx: 'auto', overflow: 'hidden' }}>
                <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                    style={{
                        height: '100%',
                        background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold}, ${palette.pine}, ${palette.purple})`,
                        borderRadius: 2,
                    }}
                />
            </Box>
        </motion.div>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </Box>
);

// ============================================
// MESSAGE COMPONENT
// ============================================

const Messages = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, companyData, currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioURL, setAudioURL] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [playingAudio, setPlayingAudio] = useState(null);
    const [connectedCompanies, setConnectedCompanies] = useState([]);
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const aiAutoDraftedRef = useRef(new Set());

    // ============================================
    // GET USER COMPANY ID
    // ============================================

    const getCompanyId = () => {
        return companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;
    };

    const getCompanyName = () => {
        return companyData?.companyName || userData?.companyName || companyData?.name || userData?.name || 'Your Company';
    };

    const getConversationIdentity = (chat) => {
        if (chat?.isDirectUser) {
            return {
                localId: currentUser?.uid || getCompanyId(),
                remoteId: chat.userId || chat.id,
                localName: userData?.displayName || userData?.firstName || getCompanyName(),
            };
        }
        return { localId: getCompanyId(), remoteId: chat?.id, localName: getCompanyName() };
    };

    const conversationIdFor = (chat) => {
        const { localId, remoteId } = getConversationIdentity(chat);
        return [localId, remoteId].filter(Boolean).sort().join('_');
    };

    // ============================================
    // FETCH CHATS & MESSAGES
    // ============================================

    useEffect(() => {
        const fetchChats = async () => {
            setLoading(true);
            try {
                const companyId = getCompanyId();
                if (!companyId) {
                    setLoading(false);
                    return;
                }

                // Fetch established company relationships. Different legacy flows
                // store the same established state as either accepted or connected.
                const invitationsRef = collection(db, 'invitations');
                const receivedQuery = query(
                    invitationsRef,
                    where('targetCompanyId', '==', companyId)
                );
                const snapshot = await getDocs(receivedQuery);
                
                const connected = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    if (!['accepted', 'connected'].includes(data.status)) return;
                    connected.push({
                        id: data.companyId,
                        companyName: data.companyName || 'Unknown Company',
                        status: 'connected',
                    });
                });

                // Also include established outgoing relationships.
                const sentQuery = query(
                    invitationsRef,
                    where('companyId', '==', companyId)
                );
                const sentSnapshot = await getDocs(sentQuery);
                sentSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (!['accepted', 'connected'].includes(data.status)) return;
                    if (!connected.find(c => c.id === data.targetCompanyId)) {
                        connected.push({
                            id: data.targetCompanyId,
                            companyName: data.targetCompanyName || 'Unknown Company',
                            status: 'connected',
                        });
                    }
                });

                // Recipient-controlled person-to-person chats. Only introductions where
                // the recipient explicitly enabled chat are surfaced here.
                if (currentUser?.uid) {
                    try {
                        const [sentIntroSnap, receivedIntroSnap, legacySentSnap, legacyReceivedSnap] = await Promise.all([
                            getDocs(query(collection(db, 'introductions'), where('fromUserId', '==', currentUser.uid))),
                            getDocs(query(collection(db, 'introductions'), where('toUserId', '==', currentUser.uid))),
                            getDocs(query(collection(db, 'ai_introductions'), where('senderUserId', '==', currentUser.uid))),
                            getDocs(query(collection(db, 'ai_introductions'), where('targetUserId', '==', currentUser.uid))),
                        ]);
                        const directIds = new Set();
                        const consider = (data, otherId) => {
                            const chatEnabled = Boolean(data.chatEnabled || data.recipientDecision === 'chat' || data.recipientDecision === 'both');
                            if (chatEnabled && otherId && otherId !== currentUser.uid) directIds.add(otherId);
                        };
                        sentIntroSnap.forEach(d => consider(d.data(), d.data().toUserId));
                        receivedIntroSnap.forEach(d => consider(d.data(), d.data().fromUserId));
                        legacySentSnap.forEach(d => consider(d.data(), d.data().targetUserId));
                        legacyReceivedSnap.forEach(d => consider(d.data(), d.data().senderUserId));
                        const userSnaps = await Promise.all([...directIds].map(id => getDoc(doc(db, 'users', id))));
                        userSnaps.forEach((snap, index) => {
                            const id = [...directIds][index];
                            if (connected.find(c => c.id === id)) return;
                            const data = snap.exists() ? snap.data() : {};
                            connected.push({
                                id,
                                userId: id,
                                companyName: data.displayName || data.name || data.email || 'TELCOFAIR User',
                                photoURL: data.photoURL || data.profileImage || '',
                                status: 'chat_enabled',
                                isDirectUser: true,
                            });
                        });
                    } catch (directListError) {
                        console.warn('Unable to load direct introduction chats:', directListError);
                    }
                }

                // A route may request a specific user chat, but the backend permission
                // check still controls whether that chat can be opened.
                if (location.state?.userId && !connected.find(c => c.id === location.state.userId)) {
                    let canOpenDirect = false;
                    try {
                        const permission = await introductionService.permission(companyId, location.state.userId);
                        canOpenDirect = Boolean(permission?.chatEnabled && !permission?.blocked);
                        if (!canOpenDirect) toast('Chat is locked until the introduction recipient chooses “Start Chat”.', { icon: '🔒' });
                    } catch (permissionError) {
                        console.warn('Direct chat permission check failed:', permissionError);
                    }
                    if (canOpenDirect) {
                    try {
                        const directUserSnap = await getDoc(doc(db, 'users', location.state.userId));
                        const directUserData = directUserSnap.exists() ? directUserSnap.data() : {};
                        connected.push({
                            id: location.state.userId,
                            userId: location.state.userId,
                            companyName: location.state.userName || directUserData.displayName || directUserData.name || directUserData.email || 'Connected User',
                            photoURL: directUserData.photoURL || directUserData.profileImage || '',
                            status: 'connected',
                            isDirectUser: true,
                        });
                    } catch (directUserError) {
                        console.warn('Unable to hydrate direct chat user:', directUserError);
                        connected.push({
                            id: location.state.userId,
                            userId: location.state.userId,
                            companyName: location.state.userName || 'Connected User',
                            status: 'connected',
                            isDirectUser: true,
                        });
                    }
                    }
                }

                setConnectedCompanies(connected);

                // Fetch chats for each connected company
                const chatData = [];
                for (const company of connected) {
                    const chatId = conversationIdFor(company);
                    const chatRef = collection(db, 'chats', chatId, 'messages');
                    const chatQuery = query(chatRef, orderBy('timestamp', 'desc'), limit(1));
                    const chatSnapshot = await getDocs(chatQuery);
                    
                    let lastMessage = null;
                    let unread = 0;
                    chatSnapshot.forEach((doc) => {
                        lastMessage = doc.data();
                    });

                    // Count unread messages
                    if (lastMessage) {
                        const unreadQuery = query(
                            chatRef,
                            where('read', '==', false),
                            where('senderId', '!=', company.isDirectUser ? (currentUser?.uid || companyId) : companyId)
                        );
                        const unreadSnapshot = await getDocs(unreadQuery);
                        unread = unreadSnapshot.size;
                    }

                    chatData.push({
                        ...company,
                        lastMessage: lastMessage?.text || 'No messages yet',
                        timestamp: lastMessage?.timestamp?.toDate?.() || new Date(),
                        unread,
                        chatId,
                    });
                }

                chatData.sort((a, b) => b.timestamp - a.timestamp);
                setChats(chatData);
                
                const totalUnread = chatData.reduce((sum, c) => sum + c.unread, 0);
                setUnreadCount(totalUnread);

                // Select first chat if none selected
                if (chatData.length > 0 && !selectedChat) {
                    setSelectedChat(chatData[0]);
                }

                // Handle opening from navigation state
                if (location.state?.company && location.state?.openChat) {
                    const company = location.state.company;
                    const chat = chatData.find(c => c.id === company.id);
                    if (chat) setSelectedChat(chat);
                } else if (location.state?.userId) {
                    const chat = chatData.find(c => c.id === location.state.userId);
                    if (chat) setSelectedChat(chat);
                } else if (location.state?.companyId && location.state?.openChat) {
                    const chat = chatData.find(c => c.id === location.state.companyId);
                    if (chat) setSelectedChat(chat);
                }

            } catch (error) {
                console.error('Error fetching chats:', error);
                toast.error('Failed to load chats');
            } finally {
                setLoading(false);
            }
        };

        fetchChats();

        // Real-time listener for new messages
        const companyId = getCompanyId();
        if (companyId) {
            const invitationsRef = collection(db, 'invitations');
            const receivedQ = query(invitationsRef, where('targetCompanyId', '==', companyId));
            const sentQ = query(invitationsRef, where('companyId', '==', companyId));
            const unsubscribeReceived = onSnapshot(receivedQ, () => { fetchChats(); });
            const unsubscribeSent = onSnapshot(sentQ, () => { fetchChats(); });
            return () => {
                unsubscribeReceived();
                unsubscribeSent();
            };
        }
    }, [companyData, userData, currentUser, location.state]);

    // ============================================
    // FETCH MESSAGES FOR SELECTED CHAT
    // ============================================

    useEffect(() => {
        if (!selectedChat) return;

        const companyId = getCompanyId();
        const chatId = conversationIdFor(selectedChat);
        
        const chatRef = collection(db, 'chats', chatId, 'messages');
        const q = query(chatRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messageList = [];
            snapshot.forEach((doc) => {
                messageList.push({ id: doc.id, ...doc.data() });
            });
            setMessages(messageList);
            
            // Play notification sound for new messages
            const lastMessage = messageList[messageList.length - 1];
            if (lastMessage && lastMessage.senderId !== (selectedChat?.isDirectUser ? (currentUser?.uid || companyId) : companyId)) {
                playNotificationSound();
            }
            
            // Mark messages as read
            messageList.forEach(async (msg) => {
                if (!msg.read && msg.senderId !== (selectedChat?.isDirectUser ? (currentUser?.uid || companyId) : companyId)) {
                    await updateDoc(doc(db, 'chats', chatId, 'messages', msg.id), {
                        read: true,
                    });
                }
            });

            // Update unread count
            const unread = messageList.filter(msg => !msg.read && msg.senderId !== (selectedChat?.isDirectUser ? (currentUser?.uid || companyId) : companyId)).length;
            setUnreadCount(prev => prev + unread);

            // Scroll to bottom
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        });

        return () => unsubscribe();
    }, [selectedChat]);

    // ============================================
    // CHATGPT MESSAGE ASSISTANT
    // ============================================

    const generateAiMessageDraft = async () => {
        if (!selectedChat) {
            toast.error('Select a conversation first.');
            return;
        }
        const companyId = getCompanyId();
        if (!companyId) return;
        setAiGenerating(true);
        try {
            if (selectedChat.isDirectUser) {
                const permission = await introductionService.permission(companyId, selectedChat.userId || selectedChat.id);
                if (!permission?.chatEnabled || permission?.blocked) {
                    toast.error(permission?.reason || 'Chat is not enabled by the recipient.');
                    return;
                }
            }
            const recentContext = messages.slice(-8).map((m) => ({
                sender: m.senderId === getConversationIdentity(selectedChat).localId ? 'me' : 'them',
                text: String(m.text || '').slice(0, 500),
            }));
            const draft = await generateMessage({
                companyId,
                targetUserId: selectedChat.isDirectUser ? (selectedChat.userId || selectedChat.id) : undefined,
                targetCompanyId: selectedChat.isDirectUser ? undefined : selectedChat.id,
                context: {
                    conversationWith: selectedChat.companyName || '',
                    recentMessages: recentContext,
                    existingDraft: newMessage.trim() || '',
                    instruction: newMessage.trim()
                        ? 'Rewrite the existing draft as a concise professional B2B reply that fits the conversation.'
                        : 'Draft the next concise professional B2B message that naturally continues this conversation.',
                },
                tone: 'professional',
            });
            const text = draft?.text || draft?.message || draft?.draft || '';
            if (!text) throw new Error('ChatGPT did not return a draft.');
            setNewMessage(text);
            toast.success('AI draft generated. Review it before sending.');
        } catch (error) {
            console.error('AI message generation failed:', error);
            toast.error(friendlyError(error, 'Unable to generate AI message.'));
        } finally {
            setAiGenerating(false);
        }
    };

    // Automatically prepare one editable ChatGPT draft the first time an
    // AI-enabled conversation is opened. It is never sent automatically.
    useEffect(() => {
        let cancelled = false;
        const prepareAutoDraft = async () => {
            if (!selectedChat || newMessage.trim() || aiGenerating) return;
            const companyId = getCompanyId();
            if (!companyId) return;
            const conversationKey = conversationIdFor(selectedChat);
            if (!conversationKey || aiAutoDraftedRef.current.has(conversationKey)) return;

            try {
                const integration = await getIntegrationSettings(companyId);
                const ai = integration?.settings?.ai || {};
                if (!ai.enabled || ai.autoGenerateMessages === false) return;
                aiAutoDraftedRef.current.add(conversationKey);
                if (!cancelled) await generateAiMessageDraft();
            } catch (error) {
                // Smart compose is an enhancement. A configuration/network error
                // must never prevent the user from writing and sending manually.
                console.warn('Automatic ChatGPT draft skipped:', error?.message || error);
            }
        };
        // Let the current conversation snapshot settle so recent messages can be
        // included as context in the automatically generated draft.
        const timer = window.setTimeout(prepareAutoDraft, 350);
        return () => { cancelled = true; window.clearTimeout(timer); };
        // Deliberately key this to the selected conversation, not every message
        // update, to avoid repeated AI requests while a chat is open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChat?.id, selectedChat?.userId]);

    // ============================================
    // SEND MESSAGE
    // ============================================

    const sendMessage = async (text, fileURL = null, fileName = null, fileType = null, fileAsset = null) => {
        if (!text.trim() && !fileURL) return;
        if (!selectedChat) {
            toast.error('No chat selected');
            return;
        }

        const companyId = getCompanyId();
        const companyName = getCompanyName();
        const chatId = conversationIdFor(selectedChat);
        const identity = getConversationIdentity(selectedChat);

        try {
            if (selectedChat.isDirectUser) {
                const permission = await introductionService.permission(companyId, selectedChat.userId || selectedChat.id);
                if (!permission?.chatEnabled || permission?.blocked) {
                    toast.error(permission?.reason || 'Chat permission is no longer active.');
                    return;
                }
            }
            const messageData = {
                text: text || '',
                senderId: identity.localId,
                senderName: identity.localName || companyName,
                receiverId: identity.remoteId,
                receiverName: selectedChat.companyName,
                timestamp: serverTimestamp(),
                read: false,
                fileURL: fileURL || null,
                fileName: fileName || null,
                fileType: fileType || null,
                fileAsset: fileAsset || null,
            };

            await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
            
            setNewMessage('');
            
            // Play notification sound for sent message
            playNotificationSound();

        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        }
    };

    // ============================================
    // HANDLE FILE UPLOAD
    // ============================================

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const companyId = getCompanyId();
            const kind = file.type?.startsWith('image/') ? 'image' : file.type?.startsWith('video/') ? 'video' : file.type?.startsWith('audio/') ? 'audio' : 'document';
            const asset = await uploadFile({ companyId, file, kind, folder: 'chats/files' });
            
            await sendMessage(`📎 ${file.name}`, asset.url, file.name, file.type, asset);
            toast.success('File uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
            fileInputRef.current.value = '';
        }
    };

    // ============================================
    // RECORD AUDIO
    // ============================================

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioURL = URL.createObjectURL(audioBlob);
                setAudioBlob(audioBlob);
                setAudioURL(audioURL);
                setIsRecording(false);
                setRecordingTime(0);
                clearInterval(recordingTimerRef.current);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);
            
            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            toast.error('Unable to access microphone');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
    };

    const sendAudioMessage = async () => {
        if (!audioBlob) return;

        setUploading(true);
        try {
            const companyId = getCompanyId();
            const audioFile = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
            const asset = await uploadFile({ companyId, file: audioFile, kind: 'audio', folder: 'chats/audio' });
            
            await sendMessage('🎤 Voice message', asset.url, 'Voice Message.webm', 'audio/webm', asset);
            setAudioBlob(null);
            setAudioURL(null);
            toast.success('Voice message sent');
        } catch (error) {
            console.error('Error sending audio:', error);
            toast.error('Failed to send voice message');
        } finally {
            setUploading(false);
        }
    };

    // ============================================
    // DELETE MESSAGE
    // ============================================

    const deleteMessage = async () => {
        if (!selectedMessage || !selectedChat) return;
        if (String(selectedMessage.senderId || '') !== String(getUserId() || '')) {
            toast.error('You can only delete messages that you sent.');
            setShowDeleteDialog(false);
            return;
        }
        
        try {
            const companyId = getCompanyId();
            const chatId = conversationIdFor(selectedChat);
            
            // Delete the uploaded asset using the provider that created it.
            if (selectedMessage.fileAsset) {
                try {
                    await deleteFile({ companyId, asset: selectedMessage.fileAsset });
                } catch (e) {
                    console.log('File may already be deleted');
                }
            }
            
            await deleteDoc(doc(db, 'chats', chatId, 'messages', selectedMessage.id));
            toast.success('Message deleted');
            setShowDeleteDialog(false);
            setSelectedMessage(null);
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Failed to delete message');
        }
    };

    // ============================================
    // PLAY AUDIO
    // ============================================

    const playAudio = (url) => {
        if (playingAudio === url) {
            setPlayingAudio(null);
            return;
        }
        setPlayingAudio(url);
        const audio = new Audio(url);
        audio.play();
        audio.onended = () => setPlayingAudio(null);
    };

    // ============================================
    // FORMAT TIME
    // ============================================

    const formatTime = (date) => {
        if (!date) return '';
        const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (date) => {
        if (!date) return '';
        const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
        const today = new Date();
        if (d.toDateString() === today.toDateString()) return 'Today';
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return d.toLocaleDateString();
    };

    // ============================================
    // RENDER
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage: `
            radial-gradient(circle at 8% 0%, rgba(193,84,31,0.10), transparent 42%),
            radial-gradient(circle at 96% 8%, rgba(47,89,66,0.08), transparent 40%),
            radial-gradient(circle at 50% 100%, rgba(184,132,42,0.06), transparent 45%)
        `,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.5,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.035%27/%3E%3C/svg%3E")',
        },
    };

    if (loading) return <LoadingSpinner label="Loading messages…" />;

    const filteredChats = chats.filter(chat =>
        chat.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box className="erp-page-shell erp-internal-page tf-messages-page" sx={canvasSx}>
            <style>{fontImport}</style>
            <Header />

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 1.75, md: 2.25 }, px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, position: 'relative', width: '100%', maxWidth: '1600px', mx: 'auto' }}>
                <Box className="erp-page-hero" sx={{ mb: 2.5 }}>
                    <Typography className="erp-page-title">Messages</Typography>
                    <Typography className="erp-page-subtitle">Conversations with your connected companies and users.</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.4, height: 'calc(100vh - 205px)', minHeight: 430 }}>
                    {/* Chat List */}
                    <Paper className="tf-message-list-panel" elevation={0} sx={{
                        width: { xs: '100%', md: '32%' },
                        borderRadius: '20px',
                        border: `1px solid ${palette.hairline}`,
                        background: palette.paper,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        {/* Chat List Header */}
                        <Box sx={{ p: 1.5, borderBottom: `1px solid ${palette.hairline}` }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 1.5 }}>
                                Messages
                                <Chip 
                                    label={`${unreadCount} unread`}
                                    size="small"
                                    sx={{ ml: 1, bgcolor: `${palette.rust}12`, color: palette.rust, fontWeight: 600 }}
                                />
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search conversations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                slotProps={{ input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: palette.inkSoft, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: '12px', bgcolor: palette.canvas }
                                } }}
                            />
                        </Box>

                        {/* Chat List Items */}
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {filteredChats.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <ChatIcon sx={{ fontSize: 48, color: palette.inkSoft, opacity: 0.3 }} />
                                    <Typography sx={{ color: palette.inkSoft, mt: 2 }}>
                                        {searchTerm ? 'No conversations found' : 'No messages yet'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                        Connect with companies to start chatting
                                    </Typography>
                                </Box>
                            ) : (
                                filteredChats.map((chat) => (
                                    <ListItemButton
                                        key={chat.id}
                                        selected={selectedChat?.id === chat.id}
                                        onClick={() => setSelectedChat(chat)}
                                        sx={{
                                            borderLeft: `3px solid ${selectedChat?.id === chat.id ? palette.rust : 'transparent'}`,
                                            bgcolor: selectedChat?.id === chat.id ? `${palette.rust}06` : 'transparent',
                                            '&:hover': { bgcolor: `${palette.rust}04` },
                                            py: 1.5,
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: palette.rust }}>
                                                {chat.companyName?.charAt(0) || 'C'}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                        {chat.companyName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                        {formatTime(chat.timestamp)}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                                                    <Typography variant="body2" sx={{ 
                                                        color: palette.inkSoft, 
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '70%',
                                                        fontWeight: chat.unread > 0 ? 600 : 400,
                                                    }}>
                                                        {chat.lastMessage}
                                                    </Typography>
                                                    {chat.unread > 0 && (
                                                        <Badge badgeContent={chat.unread} color="error" />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                ))
                            )}
                        </Box>
                    </Paper>

                    {/* Chat Area */}
                    <Paper className="tf-message-chat-panel" elevation={0} sx={{
                        flex: 1,
                        borderRadius: '20px',
                        border: `1px solid ${palette.hairline}`,
                        background: palette.paper,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        {selectedChat ? (
                            <>
                                {/* Chat Header */}
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderBottom: `1px solid ${palette.hairline}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    bgcolor: palette.canvasAlt,
                                }}>
                                    <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                                        <Avatar sx={{ bgcolor: palette.rust, width: 40, height: 40 }}>
                                            {selectedChat.companyName?.charAt(0) || 'C'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.ink }}>
                                                {selectedChat.companyName}
                                            </Typography>
                                            <Box sx={{ alignItems: 'center', gap: 0.5, display: 'flex' }}>
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.pine }} />
                                                <Typography variant="caption" sx={{ color: palette.pine, fontWeight: 600 }}>
                                                    Connected
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ gap: 1, display: 'flex' }}>
                                        <Tooltip title="Schedule Meeting">
                                            <IconButton 
                                                onClick={() => navigate('/dashboard/meetings/schedule', { state: { company: selectedChat } })}
                                                sx={{ color: palette.rust }}
                                            >
                                                <EventIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View Company">
                                            <IconButton 
                                                onClick={() => navigate(`/dashboard/company/${selectedChat.id}`)}
                                                sx={{ color: palette.teal }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Messages */}
                                <Box sx={{ 
                                    flex: 1, 
                                    overflowY: 'auto', 
                                    p: 1.6,
                                    bgcolor: palette.canvas,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}>
                                    {messages.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <ChatIcon sx={{ fontSize: 48, color: palette.inkSoft, opacity: 0.3 }} />
                                            <Typography sx={{ color: palette.inkSoft, mt: 2 }}>
                                                No messages yet
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                                Send a message to start the conversation
                                            </Typography>
                                        </Box>
                                    ) : (
                                        messages.map((msg, index) => {
                                            const isOwn = msg.senderId === getCompanyId();
                                            const showDate = index === 0 || formatDate(msg.timestamp) !== formatDate(messages[index - 1]?.timestamp);
                                            
                                            return (
                                                <React.Fragment key={msg.id}>
                                                    {showDate && (
                                                        <Box sx={{ textAlign: 'center', my: 2 }}>
                                                            <Chip 
                                                                label={formatDate(msg.timestamp)}
                                                                size="small"
                                                                sx={{ bgcolor: 'rgba(33,26,20,0.05)', color: palette.inkSoft }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: isOwn ? 'flex-end' : 'flex-start',
                                                        mb: 0.5,
                                                    }}>
                                                        <Box sx={{ 
                                                            maxWidth: '70%',
                                                            position: 'relative',
                                                        }}>
                                                            <Paper elevation={0} sx={{
                                                                p: 1.5,
                                                                borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                                bgcolor: isOwn ? palette.rust : palette.paper,
                                                                color: isOwn ? '#fff' : palette.ink,
                                                                border: isOwn ? 'none' : `1px solid ${palette.hairline}`,
                                                                position: 'relative',
                                                            }}>
                                                                {/* File/Image Display */}
                                                                {msg.fileURL && (
                                                                    <Box sx={{ mb: 1 }}>
                                                                        {msg.fileType?.startsWith('image/') ? (
                                                                            <img 
                                                                                src={msg.fileURL} 
                                                                                alt={msg.fileName || 'Image'}
                                                                                style={{ 
                                                                                    maxWidth: '100%', 
                                                                                    maxHeight: 200, 
                                                                                    borderRadius: '8px',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                                onClick={() => openSafeExternalUrl(msg.fileURL)}
                                                                            />
                                                                        ) : msg.fileType?.startsWith('audio/') ? (
                                                                            <Box sx={{ 
                                                                                display: 'flex', 
                                                                                alignItems: 'center', 
                                                                                gap: 1,
                                                                                p: 1,
                                                                                bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'rgba(33,26,20,0.05)',
                                                                                borderRadius: '8px',
                                                                            }}>
                                                                                <IconButton 
                                                                                    size="small"
                                                                                    onClick={() => playAudio(msg.fileURL)}
                                                                                    sx={{ color: isOwn ? '#fff' : palette.rust }}
                                                                                >
                                                                                    {playingAudio === msg.fileURL ? <PauseIcon /> : <PlayIcon />}
                                                                                </IconButton>
                                                                                <Box sx={{ flex: 1 }}>
                                                                                    <LinearProgress 
                                                                                        variant="determinate" 
                                                                                        value={playingAudio === msg.fileURL ? 50 : 0}
                                                                                        sx={{ height: 4, borderRadius: 2 }}
                                                                                    />
                                                                                </Box>
                                                                                <Typography variant="caption" sx={{ color: isOwn ? 'rgba(255,255,255,0.7)' : palette.inkSoft }}>
                                                                                    Voice Message
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box sx={{ 
                                                                                display: 'flex', 
                                                                                alignItems: 'center', 
                                                                                gap: 1,
                                                                                p: 1,
                                                                                bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'rgba(33,26,20,0.05)',
                                                                                borderRadius: '8px',
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={() => openSafeExternalUrl(msg.fileURL)}
                                                                            >
                                                                                <FileIcon sx={{ color: isOwn ? '#fff' : palette.rust }} />
                                                                                <Typography variant="body2" sx={{ 
                                                                                    color: isOwn ? '#fff' : palette.ink,
                                                                                    flex: 1,
                                                                                    wordBreak: 'break-all',
                                                                                }}>
                                                                                    {msg.fileName || 'File'}
                                                                                </Typography>
                                                                                <DownloadIcon sx={{ fontSize: 16, color: isOwn ? 'rgba(255,255,255,0.7)' : palette.inkSoft }} />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                                
                                                                {msg.text && (
                                                                    <Typography variant="body2" sx={{ 
                                                                        color: isOwn ? '#fff' : palette.ink,
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                    }}>
                                                                        {msg.text}
                                                                    </Typography>
                                                                )}
                                                                
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'flex-end', 
                                                                    alignItems: 'center', 
                                                                    gap: 0.5,
                                                                    mt: 0.5,
                                                                }}>
                                                                    <Typography variant="caption" sx={{ 
                                                                        color: isOwn ? 'rgba(255,255,255,0.7)' : palette.inkSoft,
                                                                        fontSize: '0.6rem',
                                                                    }}>
                                                                        {formatTime(msg.timestamp)}
                                                                    </Typography>
                                                                    {isOwn && msg.read && (
                                                                        <CheckCircleIcon sx={{ fontSize: 12, color: palette.pine }} />
                                                                    )}
                                                                    {isOwn && <IconButton 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            p: 0.25,
                                                                            color: isOwn ? 'rgba(255,255,255,0.5)' : palette.inkSoft,
                                                                            '&:hover': { bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'rgba(33,26,20,0.05)' },
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedMessage(msg);
                                                                            setMessageMenuAnchor(e.currentTarget);
                                                                        }}
                                                                    >
                                                                        <MoreVertIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>}
                                                                </Box>
                                                            </Paper>
                                                        </Box>
                                                    </Box>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </Box>

                                {/* Message Input */}
                                <Box sx={{ 
                                    p: 2.5, 
                                    borderTop: `1px solid ${palette.hairline}`,
                                    bgcolor: palette.paper,
                                }}>
                                    {audioBlob ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: palette.canvas, borderRadius: '12px' }}>
                                            <IconButton onClick={() => { setAudioBlob(null); setAudioURL(null); }} sx={{ color: palette.clay }}>
                                                <CloseIcon />
                                            </IconButton>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                    Voice message ready ({recordingTime}s)
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={100} 
                                                    sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                                                />
                                            </Box>
                                            <IconButton onClick={sendAudioMessage} disabled={uploading} sx={{ color: palette.pine }}>
                                                <SendIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    style={{ display: 'none' }}
                                                    onChange={handleFileUpload}
                                                    multiple={false}
                                                />
                                                <Tooltip title={newMessage.trim() ? 'Rewrite draft with ChatGPT' : 'Generate a professional message with ChatGPT'}>
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            onClick={generateAiMessageDraft}
                                                            disabled={uploading || aiGenerating}
                                                            startIcon={aiGenerating ? <CircularProgress size={13} color="inherit" /> : <AiIcon sx={{ fontSize: 16 }} />}
                                                            sx={{
                                                                minWidth: 0,
                                                                px: 1.2,
                                                                py: .55,
                                                                borderRadius: '10px',
                                                                textTransform: 'none',
                                                                fontSize: '.7rem',
                                                                fontWeight: 800,
                                                                color: palette.rust,
                                                                bgcolor: `${palette.rust}10`,
                                                                border: `1px solid ${palette.rust}24`,
                                                                '&:hover': { bgcolor: `${palette.rust}18`, transform: 'translateY(-1px)' },
                                                            }}
                                                        >
                                                            {newMessage.trim() ? 'AI Rewrite' : 'AI Draft'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Attach file">
                                                    <IconButton 
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={uploading}
                                                        sx={{ color: palette.inkSoft }}
                                                    >
                                                        <AttachFileIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isRecording ? 'Stop recording' : 'Record voice'}>
                                                    <IconButton 
                                                        onClick={isRecording ? stopRecording : startRecording}
                                                        sx={{ 
                                                            color: isRecording ? palette.clay : palette.inkSoft,
                                                            animation: isRecording ? 'pulse 1s infinite' : 'none',
                                                        }}
                                                    >
                                                        {isRecording ? <MicOffIcon /> : <MicIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                {isRecording && (
                                                    <Chip
                                                        label={`${recordingTime}s`}
                                                        size="small"
                                                        sx={{ bgcolor: `${palette.clay}15`, color: palette.clay, fontWeight: 600 }}
                                                    />
                                                )}
                                            </Box>
                                            <TextField
                                                fullWidth
                                                multiline
                                                maxRows={4}
                                                placeholder="Type a message..."
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        sendMessage(newMessage);
                                                    }
                                                }}
                                                disabled={uploading}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        bgcolor: palette.canvas,
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => sendMessage(newMessage)}
                                                disabled={!newMessage.trim() || uploading}
                                                sx={{
                                                    borderRadius: '12px',
                                                    minWidth: '48px',
                                                    height: '48px',
                                                    p: 0,
                                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                    '&:hover': { transform: 'scale(1.05)' },
                                                    '&:disabled': { bgcolor: palette.hairline },
                                                }}
                                            >
                                                <SendIcon />
                                            </Button>
                                        </Box>
                                    )}
                                    {uploading && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
                                            <Typography variant="caption" sx={{ color: palette.inkSoft, mt: 0.5 }}>
                                                Uploading...
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ 
                                flex: 1, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                p: 4,
                            }}>
                                <ChatIcon sx={{ fontSize: 64, color: palette.inkSoft, opacity: 0.3 }} />
                                <Typography variant="h5" sx={{ color: palette.ink, mt: 2, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                    No Chat Selected
                                </Typography>
                                <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 1 }}>
                                    Select a conversation from the list or connect with a company
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/dashboard/companies')}
                                    sx={{
                                        mt: 3,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    }}
                                >
                                    Find Companies
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Container>

            {/* Message Menu */}
            <Menu
                anchorEl={messageMenuAnchor}
                open={Boolean(messageMenuAnchor)}
                onClose={() => setMessageMenuAnchor(null)}
            >
                <MenuItem onClick={() => { setMessageMenuAnchor(null); setShowDeleteDialog(true); }}>
                    <DeleteIcon sx={{ fontSize: 18, color: palette.clay, mr: 1 }} />
                    Delete Message
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>Delete Message</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this message? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={deleteMessage} sx={{ color: palette.clay }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                `}
            </style>
        </Box>
    );
};

export default Messages;